import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ArthaGateway from 'service/ArthaGateway';
import { CreditNote, Sale, SaleOrder, Receipt, VoucherSummary, Party } from 'service';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { faCalendar, faFileInvoice, faPhone, faShippingFast, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { durations } from 'service/Utils';

export const Invoices = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [party, setParty] = useState<Party>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [durationId, setDurationId] = useState<string>(moment().month() > 3 ? moment().year().toString() : moment().subtract(1, 'year').year().toString());
    const [sales, setSales] = useState<Sale[]>([]);
    const [voucherSummary, setVoucherSummary] = useState<VoucherSummary>();
    const [voucherType, setVoucherType] = useState<string>("sale");
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(5);
    const [orderSummary, setOrderSummary] = useState<any[]>([]);

    useEffect(() => {
        if (user.profile) {
            setParty(user.profile.profile);
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadSales();
    }, [party, pageNumber]);

    useEffect(() => {
        setSales([]);
        loadSales();
    }, [durationId]);

    const loadSales = () => {
        if (!party || !party.id || party.id <= 0) {
            return;
        }
        setIsLoading(true);
        ArthaGateway
            .getSales(party?.id, durationId, 0, pageNumber, pageSize)
            .then((data) => {
                data.entries.map((sale) => sale.isExpanded = false);
                if (pageNumber > 1) {
                    setSales(sales.concat(data.entries));
                }
                else {
                    setSales(data.entries);
                }
                setTotalPages(data.totalPages);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const loadSalesSummary = () => {
        if (!party || !party.id || party.id <= 0) {
            return;
        }
        setIsLoading(true);
        ArthaGateway
            .getSalesSummary(party?.id, durationId, 0)
            .then((data) => {
                setVoucherSummary(data);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const sendWhatsAppMessage = (sale: Sale) => {
        ArthaGateway
            .getSalesFileLink(sale.id)
            .then((url) => {
                const message = "Dear " + sale.party.name + ",\r\nYour invoice *" + sale.voucherNumber + "* with amount *" + currencyFormat(sale.billAmount) + "/-* is ready. \r\nView details here " + url + ".\r\n\r\nThanks & Regards,\r\nAmruth Organic Fertilizers, Malladihalli";
                let whatsappUrl = 'https://wa.me/+91'
                    + sale.party.mobileNumber
                    + '?text='
                    + encodeURIComponent(message);
                window.open(whatsappUrl, "_blank");
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const downloadInvoice = (sale: Sale) => {
        ArthaGateway
            .getSalesFileLink(sale.id)
            .then((url) => {
                window.open(url, "_blank");
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const currencyFormat = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page-receipts'>
            <div className="row">
                <div className="col-md-12">
                    <div className="card bg-dark text-white mb-2">
                        <div className="card-header">
                            <h2 className="fw-bold mb-4">Invoices</h2>
                            <div>
                                <select className="form-control" id="duration" value={durationId} onChange={(e) => setDurationId(e.target.value)}>
                                    {durations.map((duration) => (
                                        <option key={duration.value} value={duration.value}>{duration.label}</option>
                                    ))}
                                    <option value="-1">Other</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className='col-12 cold-md-6 col-lg-4'>
                            {isLoading &&
                                <div className="text-center" style={{ padding: 20 }}>
                                    <div className="spinner-border text-dark" role="status">
                                    </div>
                                </div>
                            }
                            {sales.length === 0 && !isLoading &&
                                <div className="alert alert-danger text-center" style={{ margin: 10 }}>
                                    No sales found
                                </div>
                            }
                            {sales && sales.map((sale) => (
                                <div className='card my-2' key={sale.id}>
                                    <div className="card-header text-center">
                                        <h2 className="fw-bold">{sale.voucherNumber}</h2>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <tr className="align-middle">
                                                    <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                        <FontAwesomeIcon icon={faCalendar} />
                                                    </td>
                                                    <td colSpan={2}>
                                                        Date:
                                                        <div className="fw-bold">{moment(sale.date).format("DD MMM YYYY")}</div>
                                                    </td>
                                                </tr>
                                                <tr className="align-middle">
                                                    <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                        <FontAwesomeIcon icon={faFileInvoice} />
                                                    </td>
                                                    <td colSpan={2}>
                                                        Amount:
                                                        <div className="fw-bold">{currencyFormat(sale.billAmount)}/-</div>
                                                    </td>
                                                </tr>
                                                {sale.vehicleNumber && (
                                                    <tr className="align-middle">
                                                        <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                            <FontAwesomeIcon icon={faShippingFast} />
                                                        </td>
                                                        <td colSpan={2}>
                                                            Vehicle:
                                                            <div className="fw-bold">{sale.vehicleNumber}</div>
                                                        </td>
                                                    </tr>
                                                )}                                            {sale.driverName && (
                                                    <tr className="align-middle">
                                                        <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                            <FontAwesomeIcon icon={faUser} />
                                                        </td>
                                                        <td colSpan={2}>
                                                            Driver:
                                                            <div className="fw-bold">{sale.driverName}</div>
                                                        </td>
                                                    </tr>
                                                )}
                                                {sale.driverName && (
                                                    <tr className="align-middle">
                                                        <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                            <FontAwesomeIcon icon={faPhone} />
                                                        </td>
                                                        <td>
                                                            Driver Number:
                                                            <div className="fw-bold">{sale.driverNumber}</div>
                                                        </td>
                                                        <td style={{ width: "120px", textAlign: "right" }}>
                                                            <a
                                                                className="btn btn-dark btn-sm"
                                                                href={"tel:" + sale.driverNumber}
                                                            >
                                                                Call
                                                            </a>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="card-footer">
                                        <div className="btn btn-dark w-100" title="View" onClick={() => downloadInvoice(sale)}>
                                                <FontAwesomeIcon icon={faFileInvoice} style={{ width: "auto" }} />
                                                &nbsp; Download Invoice
                                            </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="card-footer">
                            {(totalPages > pageNumber) && <>
                                {isLoading &&
                                    <div className="btn btn-dark w-100 text-center">
                                        <div className="spinner-border text-light" role="status">
                                        </div>
                                    </div>
                                }
                                {!isLoading &&
                                    <div className="btn btn-dark w-100 text-center" onClick={() => setPageNumber(pageNumber + 1)}>Load More...</div>
                                }
                            </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};